import { getStorage, setStorage } from '@/storage';
import {
	timeInSeconds,
	getSelectorString,
	getTextContent,
	getClickProps,
	debounce,
	getClickableElement,
} from '@/utils';
import { getTrailEnabled } from '@/config';
import createListenerStore from './createListenerStore';

function trailEntry({ event = '', fields = {}, props = {} }) {
	if (!event) {
		return;
	}

	return {
		event,
		timestamp: new Date().toISOString(),
		...fields,
		props,
	};
}

const TRAIL_MAX_AGE_MS = 30 * 60 * 1000; //30 minutes

function cleanTrailEntries(entries) {
	const cutoff = Date.now() - TRAIL_MAX_AGE_MS;
	return entries.filter(entry => new Date(entry.timestamp).getTime() >= cutoff);
}

function initTrailData() {
	const sessionTrail = getStorage('trail', 'sessionStorage') ?? {};

	if (Object.keys(sessionTrail).length > 0) {
		sessionTrail.previous = cleanTrailEntries([...sessionTrail.previous, sessionTrail.current]);
		sessionTrail.current = {
			url: document.location.href,
			title: document.title,
			timestamp: new Date().toISOString(),
			events: [],
		};
		setStorage('trail', sessionTrail, 'sessionStorage');
		return sessionTrail;
	}

	return {
		current: {
			url: document.location.href,
			title: document.title,
			timestamp: new Date().toISOString(),
			events: [],
		},
		previous: [],
	};
}

const scrollZones = [25, 50, 75, 100];

export default function createGlobalListenerStore({ onLocationChange = () => {}, onSessionChange = () => {} } = {}) {
	const state = {
		currentLocation: document.location.href,
		previousLocation: getStorage('previousLocation', 'sessionStorage') ?? document.referrer,
		session: typeof getStorage('session') === 'number' ? getStorage('session') : timeInSeconds(),
		trail: initTrailData(),
	};
	let currentScrollZone = 0;

	const mopinionEventHandlers = {
		mopinion_shown: handleMopinionEvent,
		mopinion_hidden: handleMopinionEvent,
		mopinion_feedback_sent: handleMopinionEvent,
	};

	const store = createListenerStore();

	function trackPageview() {
		state.trail.previous = cleanTrailEntries([...state.trail.previous, state.trail.current]);
		state.trail.current = {
			url: document.location.href,
			title: document.title,
			timestamp: new Date().toISOString(),
			events: [],
		};
		setStorage('trail', state.trail, 'sessionStorage');

		currentScrollZone = 0;
	}

	function trackEvent({ event, fields, props } = {}) {
		if (!event) {
			console.warn('No event provided to trackEvent');
			return;
		}
		state.trail.current.events.push(trailEntry({ event, fields, props }));
		setStorage('trail', state.trail, 'sessionStorage');
	}

	function handleClick(event) {
		const { target } = event;

		//Dont track clicks in Mopinion forms
		if (target?.closest('.mopinion-survey-content')) {
			return;
		}

		trackEvent({
			event: 'click',
			fields: {
				selector: getSelectorString(target),
				text: getTextContent(getClickableElement(target)),
			},
			props: getClickProps(event),
		});
	}

	const handleScroll = debounce(() => {
		const depthPct = Math.round(((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight) * 100);

		const zone = scrollZones.findLast(z => depthPct >= z) ?? 0;

		if (zone !== currentScrollZone) {
			currentScrollZone = zone;
			trackEvent({
				event: 'scroll',
				fields: { scroll_depth: zone },
			});
		}
	}, 100);

	function handleMopinionEvent({ detail = {} } = {}) {
		trackEvent({
			event: 'mopinion',
			fields: {
				form_event: detail.event,
				form_key: detail.key,
				form_name: detail.formName,
			},
		});
	}

	function setMopinionEventListeners() {
		Object.keys(mopinionEventHandlers).forEach(event => {
			document.addEventListener(event, mopinionEventHandlers[event]);
		});
	}

	function clearMopinionEventListeners() {
		Object.keys(mopinionEventHandlers).forEach(event => {
			document.removeEventListener(event, mopinionEventHandlers[event]);
		});
	}

	function setListeners() {
		store.setIntervalListener(() => {
			const didChange = state.currentLocation !== document.location.href;
			if (didChange) {
				trackPageview();
				state.previousLocation = state.currentLocation;
				state.currentLocation = document.location.href;
				setStorage('previousLocation', state.previousLocation, 'sessionStorage');
				onLocationChange();
			}
		}, 1000);

		store.setIntervalListener(() => {
			state.session = timeInSeconds();
			setStorage('session', state.session);
			onSessionChange();
		}, 30000);

		document.addEventListener('click', handleClick);
		document.addEventListener('scroll', handleScroll);
		setMopinionEventListeners();
	}

	function clearListeners() {
		document.removeEventListener('click', handleClick);
		document.removeEventListener('scroll', handleScroll);
		clearMopinionEventListeners();
		store.clearListeners(true);
	}

	function getTrail() {
		if (!getTrailEnabled()) {
			return null;
		}
		return [...state.trail.previous, state.trail.current];
	}

	return {
		setListeners,
		clearListeners,
		getCurrentLocation: () => state.currentLocation,
		getPreviousLocation: () => state.previousLocation,
		getSession: () => state.session,
		getTrail,
		trackEvent: ({ event, props }) => trackEvent({ event: 'custom', fields: { name: event }, props }),
	};
}
